import { type Location } from '@/models/account-management/Location';
import { type LocationReadiness } from '@/models/account-management/LocationReadiness';
import { type LocationOnboardingStatus } from '@/models/account-management/OnboardingStatus';
import { createApiResource } from '../adminApi';
import { HttpError, getJwt } from '../common';

const BASE_URL = import.meta.env.VITE_API_URL;

export interface DeletedLocation {
    id: string;
    name: string;
    slug: string;
    deletedAt?: string | null;
    deletedBy?: string | null;
}

export const locationApi = {
    // NOTE: the inherited `delete` is now a SOFT delete server-side (recoverable from the admin screen).
    ...createApiResource<Location, string>('account-management/locations'),
    /**
     * Locations for a brand, with their locales.
     *
     * The inherited `list` sends x-location-id and returns whatever that scopes to; this asks by brand,
     * which is what the content-strings screen needs to work out a brand's languages.
     */
    listByBrand: async (brandId: string): Promise<Array<{ id?: string; name: string; locale?: string | null }>> => {
        const response = await fetch(`${BASE_URL}/account-management/locations?brandId=${brandId}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${getJwt()}` },
        });

        const contentType = response.headers.get('content-type') ?? '';
        const data = contentType.includes('json') ? await response.json().catch(() => null) : null;

        if (!response.ok) throw new HttpError(response.status, data);
        return data ?? [];
    },
    listDeleted: async (brandId: string): Promise<DeletedLocation[]> => {
        const response = await fetch(`${BASE_URL}/account-management/locations/deleted?brandId=${brandId}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${getJwt()}` },
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
        return data as DeletedLocation[];
    },
    restore: async (id: string): Promise<void> => {
        const response = await fetch(`${BASE_URL}/account-management/locations/${id}/restore`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${getJwt()}` },
        });
        if (!response.ok) throw new HttpError(response.status, await response.json().catch(() => null));
    },
    hardDelete: async (id: string): Promise<void> => {
        const response = await fetch(`${BASE_URL}/account-management/locations/${id}/permanent`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getJwt()}` },
        });
        if (!response.ok) throw new HttpError(response.status, await response.json().catch(() => null));
    },
    getOnboardingPending: async (brandId: string): Promise<LocationOnboardingStatus[]> => {
        const response = await fetch(`${BASE_URL}/account-management/locations/onboarding-pending?brandId=${brandId}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${getJwt()}` },
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
        return data as LocationOnboardingStatus[];
    },
    applyOnboarding: async (id: string, location: Location): Promise<void> => {
        const response = await fetch(`${BASE_URL}/account-management/locations/${id}/onboarding`, {
            method: 'PUT',
            body: JSON.stringify(location),
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getJwt()}` },
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
    },
    sendOnboardingEmail: async (id: string): Promise<void> => {
        const response = await fetch(`${BASE_URL}/account-management/locations/${id}/onboarding-email`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${getJwt()}` },
        });
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new HttpError(response.status, data);
        }
    },
    getOnboardingSchema: async (id: string): Promise<string[]> => {
        const response = await fetch(`${BASE_URL}/account-management/locations/${id}/onboarding-schema`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${getJwt()}` },
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
        return (data?.fields ?? []) as string[];
    },
    getReadiness: async (id: string): Promise<LocationReadiness> => {
        const response = await fetch(`${BASE_URL}/account-management/locations/${id}/readiness`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${getJwt()}` },
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
        return data as LocationReadiness;
    },
    getBySlug: async (slug: string): Promise<Location> => {
        const response = await fetch(`${BASE_URL}/account-management/locations/slugs/${slug}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getJwt()}`
            }
        });
        const data = await response.json();
        if (!response.ok) throw new HttpError(response.status, data);
        return await data;
    },
    getQrCode: async (id: string): Promise<string> => {
        const response = await fetch(`${BASE_URL}/account-management/locations/${id}/qrcode`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getJwt()}`
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to load image: ${response.status}`);
        }

        const blob = await response.blob();
        return URL.createObjectURL(blob);
    },
    createQrCode: async (id: string): Promise<string> => {
        const response = await fetch(`${BASE_URL}/account-management/locations/${id}/qrcode`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${getJwt()}`
            }
        });

        const contentType = response.headers.get('content-type') ?? '';
        const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

        if (!response.ok) throw new HttpError(response.status, data);
        return await data;
    }
};
